import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { Users, BookOpen, Award, Clock } from "lucide-react";

interface BatchProgress {
  batchId: string;
  batchName: string;
  totalStudents: number;
  avgSubjectsCompleted: number;
  avgExamsPassed: number;
  avgProgress: number;
  students: {
    id: string;
    name: string;
    subjectsStarted: number;
    subjectsCompleted: number;
    examsPassed: number;
    examsFailed: number;
    progress: number;
  }[];
}

const fetchStudentProgress = async (): Promise<BatchProgress[]> => {
  const [studentsRes, batchesRes, studentSubjectsRes] = await Promise.all([
    supabase.from("students").select("id, first_name, last_name, calling_name, batch_id"),
    supabase.from("batches").select("id, name"),
    supabase.from("student_subjects").select("student_id, status, exam_result"),
  ]);

  if (studentsRes.error) throw studentsRes.error;
  if (batchesRes.error) throw batchesRes.error;
  if (studentSubjectsRes.error) throw studentSubjectsRes.error;

  const batches = batchesRes.data || [];
  const students = studentsRes.data || [];
  const studentSubjects = studentSubjectsRes.data || [];

  const batchProgressMap: Record<string, BatchProgress> = {};

  // Initialize batches
  for (const batch of batches) {
    batchProgressMap[batch.id] = {
      batchId: batch.id,
      batchName: batch.name,
      totalStudents: 0,
      avgSubjectsCompleted: 0,
      avgExamsPassed: 0,
      avgProgress: 0,
      students: [],
    };
  }

  // Add "Unassigned" batch
  batchProgressMap["unassigned"] = {
    batchId: "unassigned",
    batchName: "Unassigned",
    totalStudents: 0,
    avgSubjectsCompleted: 0,
    avgExamsPassed: 0,
    avgProgress: 0,
    students: [],
  };

  // Process each student
  for (const student of students) {
    const batchId = student.batch_id || "unassigned";
    if (!batchProgressMap[batchId]) continue;

    const studentName = student.calling_name || `${student.first_name} ${student.last_name || ''}`.trim();
    const subjects = studentSubjects.filter(ss => ss.student_id === student.id);

    const subjectsStarted = subjects.length;
    const subjectsCompleted = subjects.filter(s => s.status === "Completed").length;
    const examsPassed = subjects.filter(s => s.exam_result === "Pass").length;
    const examsFailed = subjects.filter(s => s.exam_result === "Fail").length;
    const progress = subjectsStarted > 0 ? Math.round((subjectsCompleted / subjectsStarted) * 100) : 0;

    batchProgressMap[batchId].students.push({
      id: student.id,
      name: studentName,
      subjectsStarted,
      subjectsCompleted,
      examsPassed,
      examsFailed,
      progress,
    });
    batchProgressMap[batchId].totalStudents++;
  }

  // Calculate averages
  for (const batchId of Object.keys(batchProgressMap)) {
    const batch = batchProgressMap[batchId];
    if (batch.totalStudents > 0) {
      batch.avgSubjectsCompleted = Math.round(
        batch.students.reduce((sum, s) => sum + s.subjectsCompleted, 0) / batch.totalStudents
      );
      batch.avgExamsPassed = Math.round(
        batch.students.reduce((sum, s) => sum + s.examsPassed, 0) / batch.totalStudents
      );
      batch.avgProgress = Math.round(
        batch.students.reduce((sum, s) => sum + s.progress, 0) / batch.totalStudents
      );
    }
  }

  return Object.values(batchProgressMap).filter(b => b.totalStudents > 0);
};

export const StudentProgressReport = () => {
  const [selectedBatch, setSelectedBatch] = useState<string>("all");
  const { data: batchProgress = [], isLoading } = useQuery({
    queryKey: ["student-progress-report"],
    queryFn: fetchStudentProgress,
  });

  const filteredBatches = selectedBatch === "all" 
    ? batchProgress 
    : batchProgress.filter(b => b.batchId === selectedBatch);

  const totalStudents = batchProgress.reduce((sum, b) => sum + b.totalStudents, 0);
  const overallAvgProgress = batchProgress.length > 0
    ? Math.round(batchProgress.reduce((sum, b) => sum + b.avgProgress, 0) / batchProgress.length)
    : 0;

  if (isLoading) {
    return <Card><CardContent className="p-6">Loading student progress...</CardContent></Card>;
  }

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              <div>
                <p className="text-2xl font-bold">{totalStudents}</p>
                <p className="text-xs text-muted-foreground">Total Students</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-blue-500" />
              <div>
                <p className="text-2xl font-bold">{batchProgress.length}</p>
                <p className="text-xs text-muted-foreground">Active Batches</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Award className="h-5 w-5 text-green-500" />
              <div>
                <p className="text-2xl font-bold">{overallAvgProgress}%</p>
                <p className="text-xs text-muted-foreground">Avg Progress</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-yellow-500" />
              <div>
                <p className="text-2xl font-bold">
                  {batchProgress.reduce((sum, b) => sum + b.avgSubjectsCompleted, 0)}
                </p>
                <p className="text-xs text-muted-foreground">Subjects Done</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <div className="flex justify-end">
        <Select value={selectedBatch} onValueChange={setSelectedBatch}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by batch" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Batches</SelectItem>
            {batchProgress.map(batch => (
              <SelectItem key={batch.batchId} value={batch.batchId}>
                {batch.batchName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Batch Details */}
      {filteredBatches.map(batch => (
        <Card key={batch.batchId}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">{batch.batchName}</CardTitle>
              <Badge variant="secondary">{batch.totalStudents} students</Badge>
            </div>
            <div className="flex gap-4 text-sm text-muted-foreground">
              <span>Avg Progress: {batch.avgProgress}%</span>
              <span>Avg Subjects: {batch.avgSubjectsCompleted}</span>
              <span>Avg Exams Passed: {batch.avgExamsPassed}</span>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {batch.students.slice(0, 10).map(student => (
                <div key={student.id} className="flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{student.name}</p>
                    <div className="flex gap-2 text-xs text-muted-foreground">
                      <span>{student.subjectsCompleted}/{student.subjectsStarted} subjects</span>
                      <span>•</span>
                      <span>{student.examsPassed} passed</span>
                      {student.examsFailed > 0 && (
                        <>
                          <span>•</span>
                          <span className="text-destructive">{student.examsFailed} failed</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="w-32 flex items-center gap-2">
                    <Progress value={student.progress} className="flex-1" />
                    <span className="text-xs w-8">{student.progress}%</span>
                  </div>
                </div>
              ))}
              {batch.students.length > 10 && (
                <p className="text-xs text-muted-foreground text-center">
                  +{batch.students.length - 10} more students
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
